import defer from 'lodash.defer';
import throttle from 'lodash.throttle';
import { mergeRight } from 'ramda';

/**
 * Middleware state
 */
const state = {};

/**
 * Throttle middleware:
 * Skips throttling if it's already batched or action is not defined in config
 * Merges or overwrites the value in storage (based on config, default - merge)
 * Increases current counter of batched actions
 * Runs runner, decorated by lodash/throttle
 *
 * @param action Redux AnyAction type
 *
 */
export const middleware = ({ dispatch }) => (next) => (action) => {
  // current action data
  const { type, payload, meta } = action;

  // skip if throttled or isn't specified in config
  if (meta?.isThrottled || !state[type]) {
    return next(action);
  }

  // makes action async
  defer(() => {
    // data of cur
    const { config, data, runner } = state[type];
    const { shouldMerge } = config;

    // merge data (based on config)
    if (shouldMerge && typeof data === 'object') {
      // array type
      if (Array.isArray(data)) {
        state[type].data = [ ...data, ...payload ];
      } else {
        // object type
        state[type].data = mergeRight(data, payload);
      }
    } else {
      // overwrite action data in storage (based on config)
      state[type].data = payload;
    }

    // increase counter
    state[type].counter += 1;

    // run runner
    runner(dispatch);
  });
};

/**
 * Runner creator (handler function).
 *
 * @param type - Action key (from config)
 * @param logger - Flag to enable logs (from config)
 * @param defaultValue - Default value (from config)
 *
 * @return - Function which accepts Dispatch function
 */
export const createRunner = (type, logger, defaultValue) => (dispatch) => {
  const { data } = state[type];

  // prints info of batched actions
  if (logger) {
    // eslint-disable-next-line no-console
    console.log(`Batched ${type}: ${state[type].counter}`, data);
  }

  // dispatch batched actions with isThrottled meta
  dispatch({
    type,
    payload: data,
    meta: {
      isThrottled: true,
    },
  });

  // reset state
  state[type].data = defaultValue;
  // reset counter
  state[type].counter = 0;
};

/**
 * Init function.
 * Parses config and creates middleware state
 *
 * @param actionsConfig - Action config
 *
 * @return - Middleware
 */
export const init = (actionsConfig) => {
  // go through all actions
  Object.entries(actionsConfig).forEach(([ type, value ]) => {
    // get all values of config
    const {
      throttleTime = 2000,
      defaultValue,
      logger = false,
      throttleOptions = {},
      shouldMerge = true,
    } = value;

    // set state
    state[type] = {
      counter: 0,
      config: {
        throttleTime,
        defaultValue,
        logger,
        throttleOptions,
        shouldMerge,
      },
      data: defaultValue,
      runner: throttle(
        createRunner(type, logger, defaultValue),
        throttleTime,
        throttleOptions,
      ),
    };
  });

  return middleware;
};

export default init;
